import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PageMeta } from "@/components/PageMeta";
import { Trophy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!email) return;
    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
    } else {
      setSent(true);
      toast.success(t("auth.resetSent"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero text-primary-foreground">
      <PageMeta
        title="Reset hesla — Tipomatic"
        description="Zapomněl jsi heslo? Zadej e-mail a pošleme ti odkaz na obnovení hesla k účtu Tipomatic."
        path="/forgot-password"
      />
      <div className="absolute top-3 right-3"><LanguageSwitcher /></div>
      <div className="container max-w-md px-4 py-10">
        <Link to="/auth" className="mb-8 flex items-center justify-center gap-2 text-2xl font-bold">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-accent-foreground shadow-gold">
            <Trophy className="h-5 w-5" />
          </div>
          {t("auth.title")}
        </Link>
        <p className="text-center mb-6 opacity-80">{t("auth.tagline")}</p>
        <Card className="text-foreground">
          <CardContent className="pt-6 space-y-4">
            {sent ? (
              <div className="text-center space-y-3 py-4">
                <p className="text-lg font-medium">{t("auth.checkEmail")}</p>
                <p className="text-sm text-muted-foreground">{t("auth.resetInstructions")}</p>
                <Button variant="outline" className="w-full" onClick={() => setSent(false)}>
                  {t("auth.sendAgain")}
                </Button>
              </div>
            ) : (
              <>
                <h1 className="text-lg font-semibold">{t("auth.forgotPassword")}</h1>
                <div className="space-y-2">
                  <Label>{t("auth.email")}</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                    placeholder="you@example.com"
                  />
                </div>
                <Button
                  className="w-full h-11 bg-gradient-pitch"
                  disabled={busy || !email}
                  onClick={handleSubmit}
                >
                  {t("auth.sendResetLink")}
                </Button>
              </>
            )}
            <Link
              to="/auth"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t("auth.backToSignIn")}
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
